/**
 * AuditLogs — Production-wired audit log viewer
 *
 * All data sourced from useAuditStore (no mock data).
 * Features: stats cards, filters, table, pagination, export, detail sheet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Skeleton } from '@respectly/ui';
import {
  FileText,
  Download,
  RefreshCw,
  Clock,
  Activity,
  TrendingUp,
  Shield,
  AlertCircle,
  X,
  CheckCircle2,
} from 'lucide-react';
import { useAuditStore, useActiveFiltersCount } from '../../stores/useAuditStore';
import {
  AuditLogTable,
  AuditLogFilters,
  AuditLogDetail,
  AuditExportModal,
} from '../../components/audit';
import type { SuperAdminAuditLog } from '@respectly/shared-types';

// ─── Animation variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// ─── Mini sparkline ───────────────────────────────────────────────────────────

function MiniSparkline({ data }: { data: { date: string; count: number }[] }) {
  if (!data.length) return null;
  const slice = data.slice(-7);
  const max = Math.max(...slice.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-px h-7 w-full mt-2.5">
      {slice.map((d, i) => (
        <div
          key={i}
          className="flex-1 rounded-[2px] bg-gradient-to-t from-violet-500/80 to-violet-400/40 dark:from-violet-400/70 dark:to-violet-300/30 transition-all duration-500"
          style={{ height: `${Math.max((d.count / max) * 100, 8)}%` }}
          title={`${d.date}: ${d.count}`}
        />
      ))}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

type StatColor = 'blue' | 'emerald' | 'amber' | 'violet';

const COLOR_MAP: Record<
  StatColor,
  { bg: string; icon: string; ring: string; glow: string }
> = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/25',
    icon: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-1 ring-blue-100 dark:ring-blue-800/50',
    glow: 'bg-blue-400/10 dark:bg-blue-500/15',
  },
  emerald: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/25',
    icon: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-1 ring-emerald-100 dark:ring-emerald-800/50',
    glow: 'bg-emerald-400/10 dark:bg-emerald-500/15',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/25',
    icon: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-1 ring-amber-100 dark:ring-amber-800/50',
    glow: 'bg-amber-400/10 dark:bg-amber-500/15',
  },
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-900/25',
    icon: 'text-violet-600 dark:text-violet-400',
    ring: 'ring-1 ring-violet-100 dark:ring-violet-800/50',
    glow: 'bg-violet-400/10 dark:bg-violet-500/15',
  },
};

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  color: StatColor;
  children?: React.ReactNode;
}

function StatCard({ icon: Icon, label, value, color, children }: StatCardProps) {
  const c = COLOR_MAP[color];
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-5 transition-shadow hover:shadow-md dark:hover:shadow-slate-900/60">
      {/* Corner ambient glow */}
      <div
        className={`pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full ${c.glow} blur-2xl`}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            {label}
          </p>
          <div className="mt-1.5 text-[1.85rem] font-bold tabular-nums leading-none text-slate-900 dark:text-white">
            {value}
          </div>
          {children}
        </div>

        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${c.bg} ${c.ring}`}
        >
          <Icon className={`h-5 w-5 ${c.icon}`} />
        </div>
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2.5">
          <Skeleton className="h-2.5 w-16 rounded-full" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
        <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
      </div>
    </div>
  );
}

// ─── Last refreshed badge ─────────────────────────────────────────────────────

function LastRefreshed({ date }: { date: Date | null }) {
  const [label, setLabel] = useState('');

  useEffect(() => {
    function update() {
      if (!date) return setLabel('');
      const diff = Math.floor((Date.now() - date.getTime()) / 1000);
      if (diff < 5) return setLabel('Just now');
      if (diff < 60) return setLabel(`${diff}s ago`);
      if (diff < 3600) return setLabel(`${Math.floor(diff / 60)}m ago`);
      return setLabel(
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }
    update();
    const t = setInterval(update, 10_000);
    return () => clearInterval(t);
  }, [date]);

  if (!label) return null;
  return (
    <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
      <span>Updated {label}</span>
    </div>
  );
}

// ─── Logs error banner ────────────────────────────────────────────────────────

function LogsErrorBanner({
  message,
  onDismiss,
  onRetry,
}: {
  message: string;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 px-4 py-3">
        <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            Failed to load audit logs
          </p>
          <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-0.5">{message}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onRetry}
            className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
          >
            Retry
          </button>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Stats error inline ───────────────────────────────────────────────────────

function StatsError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Could not load stats — {message}
        </p>
      </div>
      <button
        onClick={onRetry}
        className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline shrink-0"
      >
        Retry
      </button>
    </div>
  );
}

// ─── Pagination helper ────────────────────────────────────────────────────────

function buildPageRange(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AuditLogs() {
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<SuperAdminAuditLog | null>(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [logsErrorDismissed, setLogsErrorDismissed] = useState(false);
  const isInitialMount = useRef(true);

  const {
    auditLogs,
    pagination,
    filters,
    isLoadingLogs,
    logsError,
    auditStats,
    isLoadingStats,
    statsError,
    isExporting,
    loadAuditLogs,
    loadStats,
    setFilter,
    clearFilters,
    exportLogs,
  } = useAuditStore();

  const activeFiltersCount = useActiveFiltersCount();

  // Dismiss error banner when a new error arrives
  useEffect(() => {
    if (logsError) setLogsErrorDismissed(false);
  }, [logsError]);

  // Initial data load
  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    Promise.all([loadAuditLogs(), loadStats()]).then(() => {
      setLastRefreshed(new Date());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadAuditLogs(), loadStats()]);
    setLastRefreshed(new Date());
  }, [loadAuditLogs, loadStats]);

  const handlePageChange = useCallback(
    (page: number) => loadAuditLogs(page),
    [loadAuditLogs]
  );

  const handleRowClick = useCallback((log: SuperAdminAuditLog) => {
    setSelectedLog(log);
    setShowDetailSheet(true);
  }, []);

  const handleExport = useCallback(
    (format: 'csv' | 'json') => exportLogs(format),
    [exportLogs]
  );

  // Derived stats
  const topAction = auditStats
    ? (Object.entries(auditStats.logsByAction).sort((a, b) => b[1] - a[1])[0]?.[0]
        ?.replace(/_/g, ' ') ?? '—')
    : '—';

  const sevenDayAvg =
    auditStats?.recentActivity.length
      ? Math.round(
          auditStats.recentActivity.reduce((s, d) => s + d.count, 0) /
            auditStats.recentActivity.length
        )
      : 0;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* ── Header ── */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between gap-4 p-4 lg:p-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-sm">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">
                Audit Logs
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-tight truncate">
                Platform activity and security audit trail
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <LastRefreshed date={lastRefreshed} />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoadingLogs}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1.5 ${isLoadingLogs ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setShowExportModal(true)}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="space-y-5 max-w-[1600px]">

          {/* Stats */}
          <motion.div variants={itemVariants}>
            {isLoadingStats && !auditStats ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <StatCardSkeleton key={i} />
                ))}
              </div>
            ) : statsError ? (
              <StatsError
                message={statsError.message}
                onRetry={loadStats}
              />
            ) : auditStats ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  icon={FileText}
                  label="Total Logs"
                  value={auditStats.totalLogs.toLocaleString()}
                  color="blue"
                />
                <StatCard
                  icon={Clock}
                  label="Today"
                  value={auditStats.logsToday.toLocaleString()}
                  color="emerald"
                />
                <StatCard
                  icon={Activity}
                  label="Top Action"
                  value={
                    <span className="capitalize text-[1.4rem]">{topAction}</span>
                  }
                  color="amber"
                />
                <StatCard
                  icon={TrendingUp}
                  label="7-Day Avg / Day"
                  value={sevenDayAvg.toLocaleString()}
                  color="violet"
                >
                  <MiniSparkline data={auditStats.recentActivity} />
                </StatCard>
              </div>
            ) : null}
          </motion.div>

          {/* Filters */}
          <motion.div variants={itemVariants}>
            <AuditLogFilters
              filters={filters}
              onFilterChange={setFilter}
              onClearFilters={clearFilters}
              activeCount={activeFiltersCount}
            />
          </motion.div>

          {/* Logs error banner */}
          <AnimatePresence>
            {logsError && !logsErrorDismissed && (
              <LogsErrorBanner
                message={logsError.message}
                onDismiss={() => setLogsErrorDismissed(true)}
                onRetry={handleRefresh}
              />
            )}
          </AnimatePresence>

          {/* Table */}
          <motion.div variants={itemVariants}>
            <AuditLogTable
              logs={auditLogs}
              isLoading={isLoadingLogs}
              onRowClick={handleRowClick}
            />
          </motion.div>
        </div>
      </div>

      {/* ── Pagination (sticky footer) ── */}
      {pagination && pagination.totalPages > 1 && (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-500 dark:text-slate-400 tabular-nums">
              {((pagination.page - 1) * pagination.limit + 1).toLocaleString()}–
              {Math.min(pagination.page * pagination.limit, pagination.total).toLocaleString()}
              {' '}of {pagination.total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || isLoadingLogs}
                className="h-8 px-3"
              >
                ← Previous
              </Button>

              {/* Page number pills */}
              <div className="flex items-center gap-1">
                {buildPageRange(pagination.page, pagination.totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} className="w-8 text-center text-sm text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p as number)}
                      disabled={isLoadingLogs}
                      className={[
                        'h-8 min-w-[2rem] rounded-md px-2 text-sm font-medium transition-colors',
                        p === pagination.page
                          ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
                      ].join(' ')}
                    >
                      {p}
                    </button>
                  )
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages || isLoadingLogs}
                className="h-8 px-3"
              >
                Next →
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export modal */}
      <AuditExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        filters={filters}
        activeFiltersCount={activeFiltersCount}
        isExporting={isExporting}
      />

      {/* Detail sheet */}
      <AuditLogDetail
        log={selectedLog}
        isOpen={showDetailSheet}
        onClose={() => {
          setShowDetailSheet(false);
          setSelectedLog(null);
        }}
      />
    </motion.div>
  );
}
